// src/App.js avec gestion de la vérification d'email et des routes complètes
// MISE À JOUR : Ajout de la route UserSettings + CORRECTION ThemeProvider

import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged, reload, applyActionCode } from 'firebase/auth';
import { ThemeProvider } from './contexts/ThemeContext'; // Import du ThemeProvider
import './styles/theme-styles.css'; // Import des styles de thème
import Login from './components/Login';
import Home from './components/Home';
import AddRecipe from './components/AddRecipe';
import Recipe from './components/Recipe';
import RecipesList from './components/RecipesList';
import EditRecipe from './components/EditRecipe';
import MealPlanner from './components/MealPlanner';
import ShoppingList from './components/ShoppingList';
import UserSettings from './components/UserSettings';
import Catalogue from './components/Catalogue';
import UpdateBanner from './components/UpdateBanner';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      if (currentUser) {
        // Vérifier si l'utilisateur vient de vérifier son email
        // Cette vérification est basée sur l'URL (mode=verifyEmail)
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        const oobCode = urlParams.get('oobCode');

        if (mode === 'verifyEmail' && oobCode) {
          setVerificationLoading(true);

          try {
            // Essayer d'appliquer directement le code d'action
            await applyActionCode(auth, oobCode);

            // Recharger l'utilisateur pour mettre à jour son état de vérification d'email
            await reload(currentUser);

            // L'utilisateur est rechargé, son état emailVerified devrait être mis à jour
            if (currentUser.emailVerified) {
              setVerificationSuccess(true);
            }

            // Nettoyer l'URL pour supprimer les paramètres de vérification
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            );
          } catch (error) {
            console.error(
              "Erreur lors de la mise à jour de l'état de vérification:",
              error
            );
          } finally {
            setVerificationLoading(false);
          }
        }
      }

      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Composant pour routes protégées qui nécessitent une authentification
  const ProtectedRoute = ({ element }) => {
    return user ? element : <Navigate to="/" replace />;
  };

  if (loading || verificationLoading) {
    return (
      <div className="loading-screen">
        {verificationLoading
          ? 'Vérification de votre email...'
          : 'Chargement...'}
      </div>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <div className="app">
          <UpdateBanner />
          <Routes>
            {/* Route d'accueil - Login ou Home selon l'authentification */}
            <Route
              path="/"
              element={
                !user ? (
                  <Login />
                ) : (
                  <Home
                    verificationSuccess={verificationSuccess}
                    setVerificationSuccess={setVerificationSuccess}
                  />
                )
              }
            />

            {/* Routes pour les recettes */}
            <Route
              path="/add-recipe"
              element={<ProtectedRoute element={<AddRecipe />} />}
            />
            <Route
              path="/recipe/:recipeId"
              element={<ProtectedRoute element={<Recipe />} />}
            />
            <Route
              path="/edit-recipe/:recipeId"
              element={<ProtectedRoute element={<EditRecipe />} />}
            />
            <Route
              path="/recipes"
              element={<ProtectedRoute element={<RecipesList />} />}
            />

            {/* Routes pour le planning et les courses */}
            <Route
              path="/meal-planner"
              element={<ProtectedRoute element={<MealPlanner />} />}
            />
            <Route
              path="/shopping-list"
              element={<ProtectedRoute element={<ShoppingList />} />}
            />

            {/* Route pour les paramètres utilisateur */}
            <Route
              path="/settings"
              element={<ProtectedRoute element={<UserSettings />} />}
            />

            {/* Catalogue de recettes */}
            <Route
              path="/catalogue"
              element={<ProtectedRoute element={<Catalogue />} />}
            />

            {/* Route de fallback qui redirige vers la page d'accueil */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
